import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import { useMsal } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import { SortHelper } from '../../SortHelper';
import { DataHelpers } from '../../DataHelpers';
import { TokenHelper } from '../../TokenHelper';
import { customersActionCreators } from '../../store/customers';
import { dataActionCreators, DataState, Sale } from '../../store/Data';
import { TeamsState } from '../../store/TeamsState';
import { Button, Divider, InputOnChangeData, SearchBox, SearchBoxChangeEvent, Skeleton, SkeletonItem, Text } from '@fluentui/react-components';
import { DownloadSPOLinkAndNav } from '../Tools/FileDownloadHelper';
import { SharePointSVG } from '../Resources/SharePointSVG';
import { AddIcon, EditIcon, UploadIcon, useEditorStyles, useTabStyles } from '../Styles/Editor';
import { useCustomers, useInvoices, useSales, useRigths } from '../Tools/ObjectLoader';

export const SalesTab = () => {
    const data = useSelector<ApplicationState, DataState>((state: ApplicationState) => state.data);
    const { customers, customersLoading } = useCustomers();
    const { invoices, invoicesLoading } = useInvoices();
    const { sales, salesLoading } = useSales();
    const [filter, setFilter] = React.useState<string | null>(null);
    const dispatch = useDispatch();
    const { instance } = useMsal();
    const teamsState = useSelector<ApplicationState, TeamsState>((state: ApplicationState) => state.teamsState);
    const { t } = useTranslation();
    const stylesTab = useTabStyles();
    const styles = useEditorStyles();
    const { isAdmin } = useRigths();

    // Check if all needed datas are retrieven
    React.useEffect(() => {
        // Check if all needed datas are retrieven
        if (data.products === undefined && data.productsLoading === false) {
            dispatch(dataActionCreators.requestProducts(instance, teamsState));
        }
        if (data.articles === undefined && data.articlesLoading === false) {
            dispatch(dataActionCreators.requestArticles(instance, teamsState));
        }
    }, [data]);

    //    {TenantHelper.ExpiredLicenses(t) && (<Label color="yellow" content="Expired License(s)" styles={{ marginLeft: "10px" }} />)}
    const itemsHeaders: JSX.Element[] = [
        <Text weight="semibold">Date</Text>,
        <Text weight="semibold">{t('Customer')}</Text>,
        <Text weight="semibold">Article</Text>,
        <Text weight="semibold">Count</Text>,
        <Text weight="semibold">Order</Text>,
        <Text weight="semibold">Customer €</Text>,
        <Text weight="semibold">{" "}</Text>,
    ];
    //const itemsGridCols = "10% 15% 35% 8% 5% 17% 10%";
    const itemsInvoicedHeaders: JSX.Element[] = [
        <Text weight="semibold">Date</Text>,
        <Text weight="semibold">{t('Customer')}</Text>,
        <Text weight="semibold">Article</Text>,
        <Text weight="semibold">Count</Text>,
        <Text weight="semibold">Order</Text>,
        <Text weight="semibold">Customer €</Text>,
        <Text weight="semibold">{t('Invoices')}</Text>,
        <Text weight="semibold">{" "}</Text>,
    ];
    //const itemsInvoiceGridCols = "10% 15% 35% 8% 5% 7% 10% 10%";
    //const itemsTotals = "52% 48%";

    const items: JSX.Element[] = [];
    const itemsInvoiced: JSX.Element[] = [];

    if (customers === undefined || customersLoading
        || data.invoices === undefined || data.invoicesLoading
        || data.products === undefined || data.productsLoading
        || data.articles === undefined || data.articlesLoading
        || data.sales === undefined || data.salesLoading) {
        [...Array(20)].map(() => {
            items.push(<Skeleton animation="pulse">
                <SkeletonItem className={stylesTab.width15} />
            </Skeleton>
            );
            items.push(<div className={stylesTab.rootSub}>
                <Skeleton animation="pulse">
                    <SkeletonItem className={stylesTab.width15} />
                </Skeleton>

                <Skeleton animation="pulse">
                    <SkeletonItem className={stylesTab.width15} />
                </Skeleton>

            </div>);
            items.push(<Skeleton animation="pulse">
                <SkeletonItem className={stylesTab.width15} />
            </Skeleton>
            );
        });
    } else {
        let LastYear = -1;
        let LastMonth = -1;
        let MonthTotal = -1;
        let itemsInMonth: JSX.Element[] = [...itemsHeaders];
        let itemsHeaderLen = itemsInMonth.length;
        let salesToDisplay = data.sales;
        if (filter) {
            salesToDisplay = salesToDisplay.filter(s => s.description?.toLocaleLowerCase().includes(filter.toLocaleLowerCase()));
        }
        salesToDisplay
            .filter(ts => ts.invoiceid === undefined || ts.invoiceid === null)
            .sort(SortHelper.SellByDate_Desc)
            .forEach((sale) => {
                const d = sale.date ? new Date(sale.date) : new Date();
                if (d.getFullYear() !== LastYear || d.getMonth() != LastMonth) {
                    if (itemsInMonth.length > itemsHeaderLen) {
                        items.push(<div className={stylesTab.SalesGrid}>{itemsInMonth}</div>);
                    }
                    if (MonthTotal !== -1) {
                        items.push(<div className={stylesTab.SalesGridTotal}>
                            <Text>{" "}</Text>
                            <Text className={stylesTab.InlineCenteredSuccessText}>{"Total " + MonthTotal.toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                        </div>);
                    }
                    LastYear = d.getFullYear();
                    LastMonth = d.getMonth();
                    items.push(<Divider appearance="brand">{(LastMonth + 1) + "/" + LastYear}</Divider>);
                    itemsInMonth = [...itemsHeaders];
                    MonthTotal = 0;
                }
                const customer = DataHelpers.GetCustomerFromId(customers, sale.customerid);
                itemsInMonth.push(<Button appearance="transparent" className={stylesTab.TestButton}
                    onClick={() => dispatch(dataActionCreators.requestSalesQuickView(sale))}
                >{sale.date ? new Date(sale.date).toLocaleDateString() : "-"}</Button>);
                if (customer) {
                    itemsInMonth.push(<Button appearance="transparent" className={stylesTab.TestButton}
                        onClick={() => dispatch(customersActionCreators.requestCustomerQuickView(customer))}>
                        {customer.displayName ? customer.displayName : "?"}
                    </Button>);
                }
                else {
                    itemsInMonth.push(<Text>?</Text>);
                }
                const article = DataHelpers.GetArticleFromId(data, sale.articleid);
                itemsInMonth.push(<Text>{(article ? article.displayName : "?") + (sale.description ? (" (" + sale.description + ")") : "")}</Text>);
                itemsInMonth.push(<Text>{sale.count}</Text>);
                itemsInMonth.push(sale.orderSharePointFileUniqueId
                    ? <Button
                        icon={<SharePointSVG />}
                        appearance="subtle"
                        aria-label={t('ViewInSharePoint')}
                        onClick={() => {
                            TokenHelper.GetToken(instance, teamsState)
                                .then(token => {
                                    if (token !== null && sale.sellid) {
                                        DownloadSPOLinkAndNav('api/Sales/' + sale.sellid + "/DownloadSPOLink", token);
                                    }
                                });
                        }
                        } />
                    : <div />);
                const price: number = sale ? sale.unitPrice * sale.count : 0;
                MonthTotal = (MonthTotal ? MonthTotal : 0) + price;
                itemsInMonth.push(<Text>{price.toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>);
                if (isAdmin) {
                    itemsInMonth.push(<div style={{ textAlign: "right" }}>
                        <Button
                            icon={<UploadIcon />}
                            appearance="subtle"
                            aria-label="Upload"
                            onClick={() => dispatch(dataActionCreators.requestFileUploadSale(sale))} />
                        <Button
                            icon={<EditIcon />}
                            appearance="subtle"
                            aria-label={t('Edit')}
                            onClick={() => dispatch(dataActionCreators.requestSaleEdition(sale))} />
                    </div>
                    );
                }
                else {
                    itemsInMonth.push(<Text>-</Text>);
                }
            });
        if (itemsInMonth.length > itemsHeaderLen) {
            items.push(<div className={stylesTab.SalesGrid}>{itemsInMonth}</div>);
        }
        if (MonthTotal !== -1) {
            items.push(<div className={stylesTab.SalesGridTotal}>
                <Text>{" "}</Text>
                <Text className={stylesTab.InlineCenteredSuccessText}>{"Total " + MonthTotal.toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
            </div>);
        }

        LastYear = -1;
        LastMonth = -1;
        MonthTotal = -1;
        itemsInMonth = [...itemsInvoicedHeaders];
        itemsHeaderLen = itemsInMonth.length;
        salesToDisplay
            .filter(ts => ts.invoiceid !== null && ts.invoiceid !== undefined)
            .sort(SortHelper.SellByDate_Desc)
            .forEach((sale) => {
                const d = sale.date ? new Date(sale.date) : new Date();
                if (d.getFullYear() !== LastYear || d.getMonth() != LastMonth) {
                    if (itemsInMonth.length > itemsHeaderLen) {
                        itemsInvoiced.push(<div className={stylesTab.SalesGridInvoiced}>{itemsInMonth}</div>);
                    }
                    if (MonthTotal !== -1) {
                        itemsInvoiced.push(<div className={stylesTab.SalesGridTotal}>
                            <Text content=" " />
                            <Text className={stylesTab.InlineCenteredSuccessText}>{"Total " + MonthTotal.toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                        </div>);
                    }
                    LastYear = d.getFullYear();
                    LastMonth = d.getMonth();
                    itemsInvoiced.push(<Divider appearance="brand">{(LastMonth + 1) + "/" + LastYear}</Divider>);
                    itemsInMonth = [...itemsInvoicedHeaders];
                    MonthTotal = 0;
                }
                const customer = DataHelpers.GetCustomerFromId(customers, sale.customerid);
                itemsInMonth.push(<Button appearance="transparent" className={stylesTab.TestButton}
                    onClick={() => dispatch(dataActionCreators.requestSalesQuickView(sale))}
                >{sale.date ? new Date(sale.date).toLocaleDateString() : "-"}</Button>);
                if (customer) {
                    itemsInMonth.push(<Button appearance="transparent" className={stylesTab.TestButton}
                        onClick={() => dispatch(customersActionCreators.requestCustomerQuickView(customer))}>
                        {customer.displayName ? customer.displayName : "?"}
                    </Button>);
                }
                else {
                    itemsInMonth.push(<Text>?</Text>);
                }
                const article = DataHelpers.GetArticleFromId(data, sale.articleid);
                itemsInMonth.push(<Text>{(article ? article.displayName : "?") + (sale.description ? (" (" + sale.description + ")") : "")}</Text>);
                itemsInMonth.push(<Text>{sale.count}</Text>);
                itemsInMonth.push(sale.orderSharePointFileUniqueId
                    ? <Button
                        icon={<SharePointSVG />}
                        appearance="subtle"
                        aria-label={t('ViewInSharePoint')}
                        onClick={() => {
                            TokenHelper.GetToken(instance, teamsState)
                                .then(token => {
                                    if (token !== null && sale.sellid) {
                                        DownloadSPOLinkAndNav('api/Sales/' + sale.sellid + "/DownloadSPOLink", token);
                                    }
                                });
                        }
                        } />
                    : <div />);
                const price: number = sale ? sale.unitPrice * sale.count : 0;
                MonthTotal = (MonthTotal ? MonthTotal : 0) + price;
                itemsInMonth.push(<Text>{price.toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>);
                const i = DataHelpers.GetInvoiceFromInvoiceid(data, sale.invoiceid);
                itemsInMonth.push(<Button appearance="transparent" className={stylesTab.TestButton}
                    onClick={() => dispatch(dataActionCreators.requestInvoiceQuickView(i))}>
                    {i ? i.invoiceRef : "?"}
                </Button>);
                if (isAdmin) {
                    itemsInMonth.push(<div style={{ textAlign: "right" }}>
                        <Button
                            icon={<UploadIcon />}
                            appearance="subtle"
                            aria-label="Upload"
                            onClick={() => dispatch(dataActionCreators.requestFileUploadSale(sale))} />
                        <Button
                            icon={<EditIcon />}
                            appearance="subtle"
                            aria-label={t('Edit')}
                            onClick={() => dispatch(dataActionCreators.requestSaleEdition(sale))} />
                    </div>
                    );
                }
                else {
                    itemsInMonth.push(<Text>-</Text>);
                }
            });
        if (itemsInMonth.length > itemsHeaderLen) {
            itemsInvoiced.push(<div className={stylesTab.SalesGridInvoiced}>{itemsInMonth}</div>);
        }
        if (MonthTotal !== -1) {
            itemsInvoiced.push(<div className={stylesTab.SalesGridTotal}>
                <Text content=" " />
                <Text className={stylesTab.InlineCenteredSuccessText}>{"Total " + MonthTotal.toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
            </div>);
        }
    }
    return (
        <div className={stylesTab.rootSub}>
            <div className={stylesTab.MainBar}>
                <SearchBox
                    placeholder={t('Contains')}
                    onChange={(e: SearchBoxChangeEvent, data: InputOnChangeData) => { setFilter(data.value); }}
                    dismiss={{ onClick: () => { setFilter(null); } }}
                    className={styles.fluid}
                />
                <div className={stylesTab.MarginLeftAuto}>
                    <Button icon={<AddIcon />} shape="circular" title={t('Add')} onClick={() => {
                        const sale: Sale = {
                            sellid: null,
                            date: null,
                            articleid: null,
                            customerid: null,
                            count: 0,
                            unitPrice: 0,
                            vatcode : 21,
                            invoiceid: null,
                            description: null,
                            orderRef: null,
                            orderSharePointFileUniqueId: null,
                            buyerItemIdentification: null,
                            sellerItemIdentification: null,
                        };
                        dispatch(dataActionCreators.requestSaleEdition(sale));
                    }} />
                </div>
            </div>
            <h2>{t('NotInvoiced2Customer')}</h2>
            {items.map(i => i)}
            <h2>{t('Invoiced2Customer')}</h2>
            {itemsInvoiced.map(i => i)}
        </div>
    );
};